import * as yup from 'yup';
import moment from 'moment';

interface TestContextExtended {
    [key: string]: any;
}

const stripHtmlTags = (html: string) => {
    let sanitized = html ?? '';
    let previous;
    do {
        previous = sanitized;
        sanitized = sanitized.replace(/<[^>]*>?/gm, '');
    } while (sanitized !== previous);
    return sanitized;
};

const parseDate = (input: string | number | undefined) => {
    if (!input) return null;

    if (typeof input === 'string' && /^\d+$/.test(input)) {
        return moment.utc(Number(input));
    }

    return moment.utc(input);
};
/**

 * Rules:
 * - name must be provided → error on cost[index].name
 * - amount must be a non‑negative integer → error on cost[index].amount
 *
 * Must be a normal function to access Yup's TestContext (`this.createError`).
 */
function validateCurrencyRecord(this, element:{ amount: number, name: string }) {
    if(!element.name)  {
        return this.createError({
            path: `${this.path}.nameError`,
            message: 'At least one currency has to be selected',
        });
    }

    if (isNaN(element.amount)) {
        return this.createError({
            path: `${this.path}.amountError`,
            message: 'Amount cannot be empty and should be a number',
        });
    }

    if (element.amount < 0) {
        return this.createError({
            path: `${this.path}.amountError`,
            message: 'Amount cannot be negative',
        });
    }

    return true;
}

function addPrizeValidationSchema() {
    return yup.object({
        tempParams: yup.object().shape({
            end_date: yup.mixed().required()
            .test('endDateTest', 'Selected date cannot be after the promotion end date.',
            (value, testContext) => {
                const {from} = testContext as yup.TestContext & TestContextExtended
                let result = true;
                if (from[0].value.prizeStatus === 'custom' && moment(from[1].value.tempParams.promoEnd).isBefore(moment(value), 'minute')) {
                    result=false
                }
                return result
            }),
             start_date: yup.mixed().required()
            .test('startDateTest', 'Selected start date cannot be after end date.',
            (value, testContext) => {
                const {from} = testContext as yup.TestContext & TestContextExtended
                let result = true;
                if (moment(from[1].value.tempParams.end_date).isBefore(moment(value), 'minute')) {
                    result=false
                }
                return result
            })
        }),
        name: yup.mixed()
            .test('prizeNameTest', 'Name is required for selected languages',
            (value) => {
                let result = true;
                value && value.forEach((element: any) => {
                    const isEmpty = Object.values(element).some(v => v === '' || v === undefined)
                    if (isEmpty) {
                        result = false
                    }
                })
                return result
            })
            .test('prizeNameLength', 'The maximum length is 90 characters',
            (value) => {
                let result = true;
                value && value.forEach((element: any) => {
                    const isLonger = Object.values(element).some((v:any)=> v && v.length > 90);
                    if (isLonger) {
                        result = false
                    }
                })
                return result
            }),
        desc: yup.array().of(
            yup.object().test('maxLengthPerLanguage', function (obj) {
                for (const [lang, val] of Object.entries(obj || {})) {
                const plain = stripHtmlTags(val || '');
                if (plain.length > 1000) {
                    return this.createError({
                    path: `${this.path}.${lang}`,
                    message: 'The maximum length is 1000 characters',
                    });
                }
                }
                return true;
            })
        ),
        short_desc: yup.array().of(
            yup.object().test('prizeShortDescTest', function (obj) {
                for (const [lang, val] of Object.entries(obj || {})) {
                const plain = stripHtmlTags(val || '');
                if (plain.length > 250) {
                    return this.createError({
                        path: `${this.path}.${lang}`,
                        message: 'The maximum length is 250 characters',
                    });
                }
                }
                return true;
            })
        ),
        redeem_desc: yup.array().of(
            yup.object().test('prizeRedeemDescTest', function (obj) {
                for (const [lang, val] of Object.entries(obj || {})) {
                const plain = stripHtmlTags(val || '');
                if (plain.length > 1000) {
                    return this.createError({
                        path: `${this.path}.${lang}`,
                        message: 'The maximum length is 1000 characters',
                    });
                }
                }
                return true;
            })
        ),
        redemption_link: yup.string()
            .notRequired()
            .nullable()
            .test('redemptionLinkTest', 'Please enter valid redemption link',
                (value) => {
                    if (value) {
                        const emailRegex = /^[\w-]+@([\w-]+\.)+[\w-]{2,4}$/;
                        const urlRegex = /^(mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|((ftp|http|https):\/\/)?(www\.)?(?!.*(ftp|http|https|www\.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[-a-zA-Z0-9_#]+)*(\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?(\/[a-zA-Z0-9_-]+)*)$/gm;
                        let isValidEmail = emailRegex.test(value);
                        let isValidUrl = urlRegex.test(value);
                        if (!isValidEmail && !isValidUrl) {
                            return false;
                        }
                    }
                    return true;
                }),
        redemption_limit: yup.mixed()
            .test('redemptionLimit', 'Winning limit is required when option is selected',
            (value, testContext) => {
                const {parent} = testContext as yup.TestContext & TestContextExtended
                let result = true;
                if (parent.tempParams.winningLimit === true && (value === '' || value === undefined)) {
                    result=false
                }
                return result
            }),
        total_amount: yup.mixed().test(
            'totalAmountTest',
            'Total Quantity is required and must be a positive number',
            (value, testContext) => {
                const { parent } = testContext as yup.TestContext & TestContextExtended;

                const shouldValidate =
                    parent.voucher_dist === 'false' || parent.dynamic_gen === 'true';

                if (!shouldValidate) return true;

                const parsed = parseInt(value);
                return !isNaN(parsed) && parsed > 0;
            }
        ),
        total_available: yup.mixed().test(
            'totalAvailableTest',
            'Available Quantity is required, must be a positive number, and cannot exceed Total Quantity',
            (value, testContext) => {
                const { parent } = testContext as yup.TestContext & TestContextExtended;

                const shouldValidate =
                        parent.voucher_dist === 'false' || parent.dynamic_gen === 'true';

                if (!shouldValidate) return true;

                const available = parseInt(value);
                const total = parseInt(parent.total_amount);

                if (isNaN(available) || available <= 0) return false;

                if (!isNaN(total) && available > total) return false;

                return true;
            }
        ),
        cost: yup.mixed().when('tempParams', {
            is: value => value?.mechanic === 'autoRedeemCnG' || value?.mechanic === 'c&g',
            then: yup.array().of(
                yup.object().shape({
                    amount: yup.number().required('Amount can contain only positive integers'),
                    name: yup.string().required('At least one currency has to be selected'),
                }).test(
                  'currency item is not valid',
                  'at least one currency item should be filled',
                  validateCurrencyRecord
                )
            ),
            otherwise: yup.array().nullable().notRequired()
        }),
        dynamic_expiry: yup
        .string()
        .when('dynamic_gen', {
            is: 'true',
            then: yup
            .string()
            .required('Expiration date is required when using dynamic voucher mode')
            .test(
                'expiration-before-end',
                'Expiration date must not be after the promotion end date.',
                function (value, testContext) {
                const { parent } = testContext;
                const prizeStatus = parent?.tempParams?.prizeStatus;
                let endDate: string | undefined;

                if (prizeStatus === 'custom') {
                    endDate = parent?.tempParams?.end_date;
                } else if (prizeStatus === 'active' || prizeStatus === 'inactive') {
                    endDate = parent?.end_date;
                }
                if (!value || !endDate) return true;

                const selected = parseDate(value);
                const promoEnd = parseDate(endDate);

                return selected?.isValid() && promoEnd?.isValid()
                    ? selected.isSameOrBefore(promoEnd)
                    : true;
                }
            ),
            otherwise: yup.string().notRequired(),
        }),
    })
}

export { addPrizeValidationSchema }
